import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Building2 } from "lucide-react";
import { AdminAlert, AdminPill } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { ApiError, api } from "@/lib/api";
import { rememberAccountId } from "@/lib/account-workspace";
import { useAuth } from "@/lib/auth";
import { invitationAcceptPath, invitationJoinLabel } from "@/lib/invitation-share";

type AcceptMembership = {
  id: number;
  account_id: number;
  user_id: number;
  role: string;
};

type InvitationPreview = {
  contact: string;
  role: string;
  status: string;
  account_name: string;
};

async function fetchInvitationPreview(token: string): Promise<InvitationPreview> {
  const response = await window.fetch(`/api/v1/invitations/${encodeURIComponent(token)}`);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(response.status, payload);
  }
  return (payload as { invitation: InvitationPreview }).invitation;
}

function InviteShell({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-card-foreground shadow-sm">
        {children}
      </div>
    </main>
  );
}

export default function InviteAcceptPage() {
  const { token } = useParams();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadingPreview(true);
    fetchInvitationPreview(token)
      .then((invitation) => {
        if (!cancelled) setPreview(invitation);
      })
      .catch((caughtError) => {
        if (!cancelled) {
          setError(caughtError instanceof ApiError ? caughtError.message : "邀请无效或已失效");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (isLoading || loadingPreview || !token || !preview || user) return;
    const returnTo = invitationAcceptPath(token);
    const params = new URLSearchParams({ next: returnTo });
    if (preview.contact.includes("@")) {
      params.set("email", preview.contact);
    } else {
      params.set("phone", preview.contact);
    }
    navigate(`/login?${params.toString()}`, { replace: true });
  }, [isLoading, loadingPreview, token, preview, user, navigate]);

  async function accept() {
    if (!token) return;
    setAccepting(true);
    setError(null);
    try {
      const { membership } = await api.fetch<{ membership: AcceptMembership }>(
        `/invitations/${token}/accept`,
        { method: "POST" },
      );
      rememberAccountId(membership.account_id);
      setDone(true);
      navigate(`/accounts/${membership.account_id}`, { replace: true });
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "接受邀请失败，请稍后重试");
    } finally {
      setAccepting(false);
    }
  }

  if (isLoading || loadingPreview || (!user && preview && !error)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        正在加载邀请…
      </main>
    );
  }

  if (!user) {
    return (
      <InviteShell>
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">邀请已失效</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {error ?? "无法打开该邀请，请联系邀请人重新发送。"}
            </p>
          </div>
          <Button asChild className="w-full">
            <Link to="/login">去登录</Link>
          </Button>
        </div>
      </InviteShell>
    );
  }

  const joinLabel = preview ? invitationJoinLabel(preview.role) : "团队";
  const accountName = preview?.account_name ?? "团队";

  return (
    <InviteShell>
      <div className="mb-8 space-y-2">
        <p className="text-sm font-medium text-muted-foreground">团队邀请</p>
        <h1 className="text-2xl font-semibold tracking-tight">加入 {accountName}</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          你被邀请以「{joinLabel}」身份加入该账户。确认无误后即可接受。
        </p>
      </div>

      {preview ? (
        <div className="mb-6 space-y-5">
          <div className="flex items-center gap-3">
            {/* 名称 text-sm/leading-5(20) + gap-1.5(6) + pill(~20) ≈ 46 */}
            <div className="flex size-[46px] shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Building2 className="size-5" />
            </div>
            <div className="min-w-0 space-y-1.5">
              <div className="truncate font-heading text-sm font-medium leading-5">{accountName}</div>
              <AdminPill tone="primary">{joinLabel}</AdminPill>
            </div>
          </div>
          <div className="grid gap-1 border-t border-border pt-5 text-sm">
            <div className="text-xs text-muted-foreground">
              {preview.contact.includes("@") ? "邀请绑定邮箱" : "邀请绑定手机号"}
            </div>
            <div className="font-mono tracking-wide">{preview.contact}</div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {preview.contact.includes("@")
                ? "请确认当前登录账号对应此邮箱；不一致将无法接受邀请。"
                : "请确认当前登录账号对应此手机号；不一致将无法接受邀请。"}
            </p>
          </div>
        </div>
      ) : null}

      {error ? <div className="mb-4"><AdminAlert>{error}</AdminAlert></div> : null}
      {done ? (
        <div className="mb-4">
          <AdminAlert tone="success">已加入账户，正在进入工作台…</AdminAlert>
        </div>
      ) : null}

      <div className="space-y-3">
        <Button
          type="button"
          className="w-full"
          onClick={() => void accept()}
          disabled={accepting || done || !preview}
        >
          {accepting ? "加入中…" : "接受邀请"}
        </Button>
        <Button asChild type="button" variant="ghost" className="w-full text-muted-foreground">
          <Link to="/">稍后再说，返回工作台</Link>
        </Button>
      </div>
    </InviteShell>
  );
}
